import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminFeatured,
  saveFeatured,
  deleteFeatured,
  getAdminBusinesses,
} from "@/services/adminService";
import type { AdminFeatured, AdminBusiness } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

export default function FeaturedSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminFeatured(accessToken),
    [] as AdminFeatured[],
    [accessToken],
  );
  const [search, setSearch]   = useState("");
  const [busyId, setBusyId]   = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [candidates, setCandidates] = useState<AdminBusiness[]>([]);
  const [pickedSlug, setPickedSlug] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((f) =>
      [f.businessName, f.categoryName, f.districtName].join(" ").toLowerCase().includes(q),
    );
  }, [data, search]);

  const activeCount = data.filter((f) => f.isActive).length;

  async function openAdd() {
    setAddOpen(true);
    try {
      const businesses = await getAdminBusinesses(accessToken);
      setCandidates(businesses.filter((b) => b.status === "active"));
    } catch {
      setCandidates([]);
    }
  }

  async function toggleActive(row: AdminFeatured) {
    const next = !row.isActive;
    setData((prev) => prev.map((f) => (f.id === row.id ? { ...f, isActive: next } : f)));
    setBusyId(row.id);
    try {
      await saveFeatured(accessToken, { isActive: next }, row.id);
    } finally {
      setBusyId(null);
    }
  }

  async function changePosition(row: AdminFeatured, position: number) {
    setData((prev) => prev.map((f) => (f.id === row.id ? { ...f, position } : f)));
    await saveFeatured(accessToken, { position }, row.id);
  }

  async function remove(row: AdminFeatured) {
    setBusyId(row.id);
    try {
      await deleteFeatured(accessToken, row.id);
      setData((prev) => prev.filter((f) => f.id !== row.id));
    } finally {
      setBusyId(null);
    }
  }

  async function addFeatured() {
    const biz = candidates.find((c) => c.slug === pickedSlug);
    if (!biz) return;
    const nextPosition = data.length + 1;
    setData((prev) => [
      ...prev,
      {
        id: Date.now(),
        businessName: biz.name,
        slug: biz.slug,
        categoryName: biz.categoryName,
        districtName: biz.districtName,
        planSlug: biz.planSlug,
        position: nextPosition,
        views: biz.views,
        isActive: true,
        startsAt: new Date().toISOString(),
        endsAt: new Date(Date.now() + 90 * 86400000).toISOString(),
      },
    ]);
    await saveFeatured(accessToken, { slug: biz.slug, position: nextPosition, isActive: true });
    setAddOpen(false);
    setPickedSlug("");
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search featured listings…" />
        <button type="button" onClick={openAdd} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> Add featured listing
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${activeCount} active of ${data.length}`}
        </span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-star-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No featured listings yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Position</th>
                  <th className={adminTh}>Business</th>
                  <th className={adminTh}>Category / District</th>
                  <th className={adminTh}>Plan</th>
                  <th className={adminTh}>Views</th>
                  <th className={adminTh}>Status</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((f) => (
                  <tr key={f.id} className="hover:bg-background-100/40">
                    <td className={adminTd}>
                      <input
                        type="number"
                        min={1}
                        value={f.position}
                        onChange={(e) => changePosition(f, Math.max(1, Number(e.target.value) || 1))}
                        className="h-8 w-16 rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 outline-none focus:border-primary-400"
                      />
                    </td>
                    <td className={adminTd}>
                      <a href={`/business/${f.slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground-900 hover:text-primary-700 hover:underline">
                        {f.businessName}
                      </a>
                    </td>
                    <td className={adminTd}>
                      <p className="text-xs text-foreground-700">{f.categoryName}</p>
                      <p className="text-[11px] text-foreground-400">{f.districtName}</p>
                    </td>
                    <td className={adminTd}>
                      <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] font-bold capitalize text-foreground-600">{f.planSlug}</span>
                    </td>
                    <td className={`${adminTd} tabular-nums`}>{f.views.toLocaleString("en-IN")}</td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        disabled={busyId === f.id}
                        onClick={() => toggleActive(f)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                          f.isActive ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        <i className={f.isActive ? "ri-eye-line text-xs" : "ri-eye-off-line text-xs"} />
                        {f.isActive ? "Active" : "Hidden"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <button type="button" disabled={busyId === f.id} onClick={() => remove(f)} className={adminIconBtn} title="Remove from featured">
                        <i className="ri-delete-bin-line text-base text-rose-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addOpen && (
        <AdminModal
          title="Add featured listing"
          subtitle="Promote an active business to the featured placements."
          onClose={() => setAddOpen(false)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAddOpen(false)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!pickedSlug} onClick={addFeatured} className={adminBtnPrimary}>Add listing</button>
            </div>
          }
        >
          <label className={adminLabel} htmlFor="feat-biz">Business</label>
          <select id="feat-biz" value={pickedSlug} onChange={(e) => setPickedSlug(e.target.value)} className={`${adminInput} cursor-pointer`}>
            <option value="">Select an active business…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.slug}>{c.name} — {c.categoryName}</option>
            ))}
          </select>
        </AdminModal>
      )}
    </div>
  );
}